/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Box, 
  Tab, 
  Tabs, 
  Paper, 
  Typography, 
  Grid, 
  Chip,
  IconButton,
  Alert,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';
import * as manufacturingApi from '../../lib/api/manufacturing';

// 탭 컴포넌트들
import ProductionPlanTabContainer from './productionplan/ProductionPlanTabContainer';
import ProductionTabContainer from './production/ProductionTabContainer';
import CompletionTabContainer from './completion/CompletionTabContainer';
import PlanFilesTabContainer from './PlanFilesTabContainer';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`manufacturing-tabpanel-${index}`}
      aria-labelledby={`manufacturing-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ManufacturingDetailContainer = ({ projectId, planId, pageUrl }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // 상태 관리
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [planData, setPlanData] = useState(null);
  const [projectInfo, setProjectInfo] = useState(null);
  const [isMockData, setIsMockData] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentTab, setCurrentTab] = useState(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const tabIndex = parseInt(tabParam);
    if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 2) {
      return tabIndex;
    }
    return 0;
  });

  // ===========================================
  // Mock 데이터 로드
  // ===========================================
  const loadMockData = useCallback(() => {
    console.log('Mock 데이터 사용 - planId:', planId);
    
    const mockPlanData = {
      planId: planId,
      planNumber: '-',
      planName: '-',
      projectId: projectId,
      projectNumber: '-',
      projectName: '-',
      // 제품 정보
      productId: null,
      productName: '-',
      productCode: '-',
      cavity: '-',
      // 수량 정보
      targetQuantity: 0,
      planQuantity: 0,
      productionQuantity: 0,
      completedQuantity: 0,
      defectQuantity: 0,
      // 일정 정보
      planStartDate: '-',
      planEndDate: '-',
      status: '',
      statusName: '-',
      remarks: '',
      // 담당자 정보
      managerUuid: null,
      managerName: '-',
      designManagerUuid: null,
      designManagerName: '-',
      productionManagerUuid: null,
      productionManagerName: '-',
      qcManagerUuid: null,
      qcManagerName: '-',
      createdBy: null,
      createdByName: '-',
      createdAt: '',
      updatedBy: null,
      updatedByName: '-',
      updatedAt: '',
    };

    const mockProjectInfo = {
      projectNo: projectId || '-',
      customerName: '-',
      projectName: '-',
      projectType: 'n',
      projectId: projectId || '',
    };

    setPlanData(mockPlanData);
    setProjectInfo(mockProjectInfo);
    setIsMockData(true);
  }, [planId, projectId]);
  
  // ===========================================
  // 생산 계획 데이터 로드
  // ===========================================
  useEffect(() => {
    const fetchPlanData = async () => {
      if (!planId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        console.log('API 호출 - planId:', planId);
        const response = await manufacturingApi.getManufacturingPlanById(planId);
        console.log('Plan API 응답:', response.data);
        
        if (response.data.success && response.data.data) {
          const data = response.data.data;
          setPlanData(data);
          setIsMockData(false);
          
          // 프로젝트 정보 설정
          setProjectInfo({
            projectNo: data.projectNumber || projectId,
            customerName: data.customerName || '',
            projectName: data.projectName || '프로젝트명',
            projectType: data.projectType || 'n',
            projectId: data.projectId || projectId,
          });
        } else {
          // 데이터가 없으면 Mock 사용
          loadMockData();
          setError('생산 계획 데이터가 없습니다. Mock 데이터를 표시합니다.');
        }
      } catch (err) {
        console.error('생산 계획 조회 에러:', err);
        
        // API 에러 시 Mock 데이터로 폴백
        const status = err.response?.status;
        const shouldUseMock = status === 404 || status === 500 || 
                             err.code === 'ERR_NETWORK' || 
                             err.code === 'ERR_BAD_REQUEST';
        
        if (shouldUseMock) {
          console.log('API 에러 - Mock 데이터로 폴백');
          loadMockData();
          setError('서버 연결 실패 - Mock 데이터를 표시합니다.');
        } else {
          setError(err.response?.data?.message || '생산 계획을 불러오는데 실패했습니다.');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchPlanData();
  }, [planId, projectId, loadMockData, refreshKey]);
  
  // URL 파라미터가 변경될 때 탭 업데이트
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const tabIndex = parseInt(tabParam);
    
    if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 2) {
      setCurrentTab(tabIndex);
    }
  }, [location.search]);
  
  // 데이터 새로고침 핸들러
  const handleRefresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  const handleTabChange = useCallback((event, newValue) => {
    setCurrentTab(newValue);
    
    // URL 파라미터 업데이트
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', newValue);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
  }, [navigate, location]);
  
  // 로딩 중
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }
  
  // 데이터 없음 (Mock 폴백도 실패한 경우)
  if (!planData || !projectInfo) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        생산 계획을 찾을 수 없습니다.
      </Alert>
    );
  }

  // 생산 계획 목록 구성
  const productionPlanList = [
    {
      id: planData.planId,
      planCode: planData.planNumber,
      productName: planData.planName || '생산계획',
      currentVersion: 1,
      status: planData.status,
      statusName: planData.statusName,
      targetQuantity: planData.planQty || planData.targetQuantity,
      planStartDate: planData.planStartDate,
      planEndDate: planData.planEndDate,
      remarks: planData.remarks,
      workflowUuid: planData.workflowUuid,
      versionHistory: [
        {
          version: 1,
          workflowId: planData.workflowUuid || 1,
          workflowName: planData.workflowUuid ? '표준 공정' : '-',
          processSteps: {
            design: true,
            production: true,
            qc: true,
            complete: true,
          },
          startDate: planData.planStartDate,
          endDate: null,
        },
      ],
    },
  ];

  // 선택된 생산계획 ID (기본값: 첫 번째 생산계획)
  const selectedPlanId = productionPlanList[0]?.id || null;

  // 선택된 생산계획 찾기
  const selectedPlan = productionPlanList.find(plan => plan.id === selectedPlanId);

  // 현재 버전의 워크플로우 정보 가져오기
  const getCurrentWorkflow = () => {
    if (!selectedPlan || !selectedPlan.versionHistory) return null;
    return selectedPlan.versionHistory.find(
      v => v.version === selectedPlan.currentVersion
    );
  };

  const currentWorkflow = getCurrentWorkflow();

  // 생산계획 변경 핸들러
  const handlePlanChange = (event) => {
    // 현재 단일 계획만 지원하므로 비활성화
    // setSelectedPlanId(event.target.value);
  };

  // 상태별 색상
  const getStatusColor = (status) => {
    switch (status) {
      case 'PLAN': return 'info';
      case 'IN_DESIGN': return 'info';
      case 'IN_PROGRESS': return 'warning';
      case 'IN_PRODUCTION': return 'warning';
      case 'IN_QC': return 'warning';
      case 'PRODUCED': return 'success';
      case 'SHIPPED': return 'success';
      case 'COMPLETED': return 'success';
      case 'QC_FAILED': return 'error';
      case 'CLOSED': return 'default';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  // 상태 한글 레이블 (DB 공통코드 없을 때 fallback)
  // 제조 상태(IN_PRODUCTION/PRODUCED/SHIPPED/QC_FAILED 등)를 빠뜨리면 공통코드가 없는 환경에서
  // 칩에 'SHIPPED' 같은 원문 코드가 그대로 나온다 - 2026-09-11 출하 테스트에서 실제로 났다.
  const getStatusLabel = (status, statusName) => {
    if (statusName) return statusName;
    switch (status) {
      case 'PLAN': return '설계전';
      case 'IN_DESIGN': return '설계중';
      case 'IN_PROGRESS': return '진행중';
      case 'IN_PRODUCTION': return '생산중';
      case 'IN_QC': return 'QC검사중';
      case 'PRODUCED': return '생산완료';
      case 'SHIPPED': return '출하완료';
      case 'QC_FAILED': return '품질불합격';
      case 'COMPLETED': return '완료';
      case 'CLOSED': return '종결';
      case 'CANCELLED': return '취소';
      default: return status || '-';
    }
  };

  const tabLabels = ['계획', '생산', '완료', '첨부파일'];

  return (
    <>
      {/* Mock 데이터 경고 */}
      {error && (
        <Alert 
          severity={isMockData ? 'warning' : 'error'} 
          sx={{ m: 2 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* 수주 정보 헤더 */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 2, 
          mb: 2, 
          backgroundColor: '#f5f5f5',
          borderLeft: '4px solid #1976d2'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => navigate(`/manufacturingmng/${projectId}/plans`)}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="h1">
              프로젝트: {projectInfo.projectNo}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isMockData && (
              <Chip 
                label="Mock 데이터" 
                color="warning" 
                variant="outlined"
                size="small"
              />
            )}
            <Chip 
              label={getStatusLabel(planData.status, planData.statusName)} 
              color={getStatusColor(planData.status)}
              variant="filled"
            />
          </Box>
        </Box>
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={12}>
            <Typography variant="body2" color="text.secondary">
              프로젝트명
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 500,
                color: '#1976d2',
                cursor: 'pointer',
                textDecoration: 'underline',
                '&:hover': { color: '#1565c0' },
              }}
              onClick={() => {
                const basePath = projectInfo.projectType === 'm' ? '/maintancetmng' : '/projectmng';
                navigate(`${basePath}/${projectInfo.projectId}`);
              }}
            >
              {projectInfo.projectName}
            </Typography>
          </Grid>
          
          {/* 생산계획 선택 드롭다운 */}
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              생산계획
            </Typography>
            <FormControl fullWidth size="small">
              <Select
                value={selectedPlanId}
                onChange={handlePlanChange}
                displayEmpty
                sx={{ fontWeight: 500 }}
              >
                {productionPlanList.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {plan.planCode}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        - {plan.productName}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* 적용 공정 */}
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              적용 워크플로우
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {currentWorkflow ? currentWorkflow.workflowName : '-'}
              </Typography>
              {selectedPlan && selectedPlan.currentVersion > 1 && (
                <Chip 
                  label={`v${selectedPlan.currentVersion}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              )}
            </Box>
          </Grid>

          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              매출처명
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {projectInfo.customerName}
            </Typography>
          </Grid>

          {/* 추가 정보 */}
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              목표수량
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {new Intl.NumberFormat('ko-KR').format(planData.planQty || planData.targetQuantity || 0)}개
            </Typography>
          </Grid>

          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              계획 기간
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5 }}>
              {planData.planStartDate} ~ {planData.planEndDate}
            </Typography>
          </Grid>

          {/* 담당자 정보 */}
          <Grid item xs={6} md={3}>
            <Typography variant="body2" color="text.secondary">
              생산담당자
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 500, color: '#ed6c02' }}>
              {planData.productionManagerName || '-'}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* 워크플로우 버전 변경 경고 */}
      {selectedPlan && selectedPlan.currentVersion > 1 && (
        <Alert 
          severity="warning" 
          icon={<InfoIcon />}
          sx={{ mb: 2 }}
        >
          <Typography variant="body2">
            ⚠️ 워크플로우가 변경되었습니다 (v{selectedPlan.currentVersion}). 이전 버전에서 작업한 데이터는 보존됩니다.
          </Typography>
        </Alert>
      )}

      <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="제조관리 탭"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {tabLabels.map((label, index) => (
            <Tab
              key={index}
              label={label}
              id={`manufacturing-tab-${index}`}
              aria-controls={`manufacturing-tabpanel-${index}`}
            />
          ))}
        </Tabs>

        <TabPanel value={currentTab} index={0}>
          <ProductionPlanTabContainer 
            productionPlan={selectedPlan} 
            planData={planData}
            projectInfo={projectInfo}
            onRefresh={handleRefresh}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <ProductionTabContainer 
            productionPlan={selectedPlan} 
            planId={planId}
            planData={planData}
            onRoutingUpdate={handleRefresh}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <CompletionTabContainer 
            productionPlan={selectedPlan} 
            planId={planId}
            planData={planData}
            onRoutingUpdate={handleRefresh}
          />
        </TabPanel>

        {/* 단계별로 흩어져 있던 첨부파일을 이 계획 기준으로 모아 보여준다. */}
        <TabPanel value={currentTab} index={3}>
          <PlanFilesTabContainer planId={selectedPlanId || planId} />
        </TabPanel>
      </Box>
    </>
  );
};

export default ManufacturingDetailContainer;
